/**
 * Discount System Type Definitions
 * Phase 3 Frontend - Type-safe interfaces for all discount operations
 */

// ============================================================================
// ENUMS
// ============================================================================

export enum DiscountType {
  VARIABLE_FAMILY = 'VARIABLE_FAMILY',
  VARIABLE_INSTRUMENT = 'VARIABLE_INSTRUMENT',
  VARIABLE_PROGRAM_LEVEL = 'VARIABLE_PROGRAM_LEVEL',
  CUSTOM = 'CUSTOM',
}

export enum DiscountScope {
  FAMILY = 'FAMILY',
  STUDENT = 'STUDENT',
  INVOICE_ITEM = 'INVOICE_ITEM',
}

export enum DiscountStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum DiscountAuditAction {
  CREATED = 'CREATED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REMOVED = 'REMOVED',
  RECALCULATED = 'RECALCULATED',
}

// ============================================================================
// CORE MODELS
// ============================================================================

/**
 * #625: what a discount covers. Stored on every catalogue entry, every applied row and (as
 * `defaultCovers*`) the school default. The rule and the words: `utils/discountCoverage.ts`.
 */
export interface DiscountCoverage {
  coversTuition: boolean;
  /** Lines typed by hand land here unless the admin picks a kind (#372). */
  coversOther: boolean;
  /** Every product line, including a category made after the discount was. */
  coversAllProducts: boolean;
  /** A product line with no category. Ignored when `coversAllProducts`. */
  coversUncategorised: boolean;
  /** This school's ProductCategory ids. Ignored when `coversAllProducts`. */
  coversCategoryIds: string[];
}

export interface VariableDiscountSetting {
  id: string;
  schoolId: string;
  familyDiscountEnabled: boolean;
  familyDiscountPercent: number;
  familyMinStudents: number;
  instrumentDiscountEnabled: boolean;
  instrumentDiscountPercent: number;
  instrumentMinCount: number;
  programLevelDiscountEnabled: boolean;
  programLevelDiscountPercent: number;
  programLevelMinCount: number;
  /** #625 D6: what the automatic discounts cover, and the pre-fill of a new discount. */
  defaultCoversTuition: boolean;
  defaultCoversOther: boolean;
  defaultCoversAllProducts: boolean;
  defaultCoversUncategorised: boolean;
  defaultCoversCategoryIds: string[];
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CustomDiscount extends DiscountCoverage {
  id: string;
  schoolId: string;
  name: string;
  description?: string | null;
  discountType: DiscountType.CUSTOM;
  scope: DiscountScope;
  value: number;
  isPercent: boolean;
  applicableFamilies: string[];
  applicableStudents: string[];
  maxUsagePerFamily?: number | null;
  maxUsagePerStudent?: number | null;
  maxUsageTotal?: number | null;
  status: DiscountStatus;
  isStackable: boolean;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  createdById: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  CreatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

/**
 * One entry of the invoice discount preview (`GET /admin/discounts/invoice/:id/preview`).
 * ⚠️ The server sends `type`; this said `discountType` until 2026-10-02, so the
 * modal's type chip never rendered (an asserted type, not a validated one).
 */
export interface CalculatedDiscount extends DiscountCoverage {
  /** A catalogue discount's id, or a synthetic `variable-…` id (the family discount). */
  id: string;
  name: string;
  description?: string;
  type: DiscountType;
  scope: DiscountScope;
  value: number;
  isPercent: boolean;
  /** Worked out on the invoice's STORED lines. */
  calculatedAmount: number;
  baseAmount?: number;
  percentage?: number;
  isStackable?: boolean;
  /** No catalogue row behind it (the family discount). */
  isVariable?: boolean;
  /** #625 D9: what it covers, in words ("tuition", "everything"). */
  coverageLabel: string;
}

export interface DiscountPreview {
  availableDiscounts: CalculatedDiscount[];
  totalPotentialSavings: number;
  estimates: {
    subtotal: number;
    withAllDiscounts: number;
    savings: number;
  };
}

export interface DiscountAuditLog {
  id: string;
  action: DiscountAuditAction;
  invoiceId: string;
  discountName: string;
  discountValue: number;
  calculatedAmount: number;
  reason?: string | null;
  performedById: string;
  createdAt: string | Date;
  PerformedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface InvoiceDiscountDetails {
  invoiceId: string;
  discountsApprovedAt?: string | Date | null;
  approvedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  approvals: Array<DiscountCoverage & {
    discountId: string;
    discountName: string;
    amount: number;
    /** #625 D9. */
    coverageLabel: string;
    approvedAt?: string | Date | null;
    approvedBy?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
  }>;
}

export interface DiscountStatistics {
  totalDiscountsApplied: number;
  totalAmountDiscounted: number;
  averageDiscountValue: number;
  discountsByType: Record<DiscountType, number>;
  discountsByScope: Record<DiscountScope, number>;
}

// ============================================================================
// FORM INPUT TYPES
// ============================================================================

export interface UpdateVariableDiscountSettingsInput {
  familyDiscountEnabled?: boolean;
  familyDiscountPercent?: number;
  familyMinStudents?: number;
  instrumentDiscountEnabled?: boolean;
  instrumentDiscountPercent?: number;
  instrumentMinCount?: number;
  programLevelDiscountEnabled?: boolean;
  programLevelDiscountPercent?: number;
  programLevelMinCount?: number;
  /** #625 D6. Absent = unchanged. */
  defaultCoverage?: DiscountCoverage;
}

export interface CreateCustomDiscountInput {
  name: string;
  description?: string;
  scope: DiscountScope;
  value: number;
  isPercent: boolean;
  applicableFamilies?: string[];
  applicableStudents?: string[];
  maxUsagePerFamily?: number | null;
  maxUsagePerStudent?: number | null;
  maxUsageTotal?: number | null;
  status?: DiscountStatus;
  isStackable?: boolean;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  /** #625: absent = the school default on create, UNCHANGED on update. */
  coverage?: DiscountCoverage;
}

export interface UpdateCustomDiscountInput
  extends Omit<CreateCustomDiscountInput, 'scope'> {
  // scope cannot be updated
}

export interface ApproveDiscountsInput {
  selectedDiscountIds: string[];
}

/** A one-off discount typed by an admin for a single invoice (2026-09-24). */
export interface ApplyOneOffDiscountInput {
  name: string;
  description?: string;
  value: number;
  isPercent: boolean;
  scope?: DiscountScope;
  /** Also save it to the reusable catalogue, so it appears on FUTURE invoices. */
  saveToCustomDiscounts?: boolean;
  /** #625: absent = the school default. */
  coverage?: DiscountCoverage;
}

export interface RemoveDiscountInput {
  reason: string;
}

export interface GetCustomDiscountsParams {
  status?: DiscountStatus;
  skip?: number;
  take?: number;
  searchTerm?: string;
}

export interface GetStatsParams {
  dateFrom?: string | Date;
  dateTo?: string | Date;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface CustomDiscountsListResponse {
  discounts: CustomDiscount[];
  total: number;
}
